import { set, get, del, clear, keys } from "idb-keyval";
import { Config } from "./Config";

// Utility functions for encoding/decoding and base64 conversions
const encode = (str) => new TextEncoder().encode(str);
const decode = (buf) => new TextDecoder().decode(buf);
const toBase64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const fromBase64 = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

// Get a secret key for encryption, pulled from app configuration
const getSecretKey = () => Config.appUrl;

// Derive a cryptographic key from the secret and salt using PBKDF2
const deriveKey = async (secret, salt) => {
  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    encode(secret),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return window.crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: 100000, hash: "SHA-256" },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

// Encrypt data using AES-GCM with random salt and IV
const encryptData = async (data) => {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(getSecretKey(), salt);
  const encoded = encode(data);

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoded
  );

  return {
    encrypted: toBase64(encrypted),
    salt: toBase64(salt),
    iv: toBase64(iv),
  };
};

// Decrypt previously encrypted data using salt and IV
const decryptData = async ({ encrypted, salt, iv }) => {
  try {
    const key = await deriveKey(getSecretKey(), fromBase64(salt));
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fromBase64(iv) },
      key,
      fromBase64(encrypted)
    );
    return decode(decrypted);
  } catch (e) {
    console.error("Decrypt error:", e);
    return null;
  }
};

// Main secureDB object for interacting with encrypted local IndexedDB storage
export const secureDB = {
  // Store an item securely by encrypting it before saving
  async setItem(key, value) {
    const encrypted = await encryptData(
      typeof value === "string" ? value : JSON.stringify(value)
    );
    await set(key, encrypted);
  },

  // Retrieve and decrypt an item from storage
  async getItem(key) {
    const stored = await get(key);
    if (!stored) return null;

    const decrypted = await decryptData(stored);
    try {
      return JSON.parse(decrypted); // Try to parse as JSON
    } catch {
      return decrypted; // Return as string if not JSON
    }
  },

  // Get a specific property from a stored object, if it exists
  async getItemValue(key, propertyKey) {
    const data = await secureDB.getItem(key);
    if (!data || typeof data !== "object") return null;
    if (!data.hasOwnProperty(propertyKey)) return null;
    return data[propertyKey];
  },

  // Clear all data or selectively clear all except whitelisted keys
  async clear(force = false) {
    if (force === true) {
      await clear(); // Clear everything
    } else {
      const exceptKeys = ["practice_insurances", "redirect", "_veradigm_fetched_date"];
      const keysList = await keys();
      for (const key of keysList) {
        if (!exceptKeys.includes(key)) {
          await del(key);
        }
      }
    }
  },

  // Remove multiple items from storage
  async removeItems(keysList) {
    for (const key of keysList) {
      await del(key);
    }
  },

  // Remove a single item by key
  async removeItem(key) {
    await del(key);
  },

  // List all stored keys
  async listKeys() {
    return await keys();
  },

  // Search for a keyword in keys and values of a stored object
  async searchItem(key, keyword) {
    const data = await secureDB.getItem(key);
    if (!data || typeof data !== "object") return null;
    return Object.entries(data).find(
      ([k, v]) => String(k).includes(keyword) || String(v).includes(keyword)
    );
  },
};
